import { useEffect } from 'react';
import cn from 'classnames';

// Utils
import { getZipDataCookie } from 'src/utils/cookies/zipCodeTestCookie';
import { noop } from '@utils/event';

/* Store & queries  */
import useServiceSkuStore from '@scenes/ServiceSkuPage/store/store.servicesku';
import { useUserIdSelector } from '@store/queries/user/useUser.selector';
import usePartnerCache from '@integrations/partners/usePartnerQuery.cache';

/* Components */
import { Panel, Icon } from 'ht-styleguide';
import ServiceInfoUpsell from '@scenes/ServiceSkuPage/Parts/ServiceInfo/ServiceInfoUpsell';
/* Styles */
import styles from './zipcodeentry.module.scss';

/* Inline Types */
type SkuZipCodeEntrySuccessProps = {
  onUpsellToggle: BaseAnyFunction;
  onUpsellMore: BaseAnyFunction;
  resetZipCodeForm: BaseAnyFunction;
  className: string;
  serviceExtra: any;
};

type ResetZipLinkProps = {
  onClick: BaseAnyFunction;
  className: string;
};

const ResetZipLink = ({ onClick, className }: ResetZipLinkProps) => (
  <a
    href="#"
    className={cn('text-link -no-decoration text-weight-medium', styles.differentZipText, className)}
    onClick={e => {
      e.preventDefault();
      onClick();
    }}
  >
    Different ZIP Code?
  </a>
);

const SkuZipCodeEntrySuccess = ({
  className,
  serviceExtra,
  resetZipCodeForm = noop,
  onUpsellToggle = noop,
  onUpsellMore = noop,
}: SkuZipCodeEntrySuccessProps) => {
  /* Store */
  const { isZipSuccessViewActive, setZipSuccessViewActive } = useServiceSkuStore(state => state.zipCodeGate);
  const userId = useUserIdSelector();

  /* Data */
  const zipDataCookie = getZipDataCookie() || { zipCode: '' };

  /* Cache */
  const partner = usePartnerCache();

  /* Effects */
  // These effects are needed to synchronize the CTA button language and customer flow that is defined in ServicePage.js
  // On component mount set true, on dismount set false
  useEffect(() => {
    if (!isZipSuccessViewActive) setZipSuccessViewActive(true);

    return () => {
      if (isZipSuccessViewActive) setZipSuccessViewActive(false);
    };
  }, [isZipSuccessViewActive, setZipSuccessViewActive]);

  /* Now that this is shared with landings, landings doesn't want partner. Assume the same for sku. For single services */
  if (partner?.id) return null;

  return (
    <Panel mediumBorderRadius className={cn(className, styles.zipContent, styles.zipSuccess)}>
      {!userId && (
        <div className={cn('flex justifyContentSpaceBetween alignItemsCenter', styles.upsellSpacing)}>
          <div className="flex alignItemsCenter">
            <Icon name={cn('v2-green-checkmark', styles.successIcon)} />
            <p className="h5 marginLeft-tiny1">We Serve {zipDataCookie.zipCode}!</p>
          </div>
          <ResetZipLink className="showDesktop" onClick={resetZipCodeForm} />
        </div>
      )}
      <ServiceInfoUpsell serviceExtra={serviceExtra} onUpsellToggle={onUpsellToggle} onUpsellMore={onUpsellMore} className={styles.upsellSpacing} />

      {!userId && <ResetZipLink className="showMobileTablet marginTop-small text-align-center" onClick={resetZipCodeForm} />}
    </Panel>
  );
};

export default SkuZipCodeEntrySuccess;
